import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';
import AIPiggyBank from '../components/AIPiggyBank';

/**
 * Deleting an account from the website takes two steps, so knowing someone's
 * email isn't enough: the form asks the server to email a one-time link, and
 * the link opens this page again (`?u=…&t=…`) to confirm. The page says the
 * same thing whether or not the email has an account. The link's token is
 * taken out of the address bar as soon as the page reads it.
 */
type PageState = 'form' | 'requesting' | 'sent' | 'checking' | 'confirm' | 'submitting' | 'success' | 'error';

const requestDeletionFn = httpsCallable<{ email: string }, { requested: boolean }>(functions, 'deleteAccount');

const confirmDeletionFn = httpsCallable<
  { uid: string; token: string; check?: boolean },
  { success?: boolean; valid?: boolean }
>(
  functions,
  'confirmAccountDeletion',
);

export default function DeleteAccountPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once: the URL is cleared below, but the link stays in memory.
  const [link, setLink] = useState(() => {
    const uid = searchParams.get('u');
    const token = searchParams.get('t');
    return uid && token ? { uid, token } : null;
  });
  const [pageState, setPageState] = useState<PageState>(link ? 'checking' : 'form');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const expiredMessage =
    'This link is invalid or has expired. Links work once, for 1 hour. Request a new one to try again.';

  useEffect(() => {
    if (!link) return;
    setSearchParams({}, { replace: true });
    confirmDeletionFn({ ...link, check: true })
      .then(({ data }) => {
        if (data.valid) {
          setPageState('confirm');
        } else {
          setErrorMessage(expiredMessage);
          setPageState('error');
        }
      })
      // Couldn't check: let them try, and the delete call gives the real answer.
      .catch(() => setPageState('confirm'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitEmail = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');

    const trimmed = email.trim();
    if (!trimmed) {
      setFormError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    setPageState('requesting');
    try {
      await requestDeletionFn({ email: trimmed.toLowerCase() });
      setPageState('sent');
    } catch {
      setErrorMessage('Something went wrong while processing your request. Please try again later.');
      setPageState('error');
    }
  };

  const handleConfirmDeletion = async () => {
    if (!link) return;
    setPageState('submitting');

    try {
      await confirmDeletionFn(link);
      setPageState('success');
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      setErrorMessage(
        code === 'functions/permission-denied'
          ? expiredMessage
          : 'Something went wrong while deleting your account. Your link still works for now, so you can try again from the email.',
      );
      setPageState('error');
    }
  };

  const handleStartOver = () => {
    setLink(null);
    setEmail('');
    setFormError('');
    setErrorMessage('');
    setPageState('form');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #120938 0%, #241A4C 100%)' }}
    >
      {/* AI Piggy Bank Mascot */}
      <AIPiggyBank className="absolute bottom-0 right-0 w-96 h-96 opacity-25 pointer-events-none select-none transform translate-x-16 translate-y-16" />

      <div className="bg-bg-primary p-8 rounded-2xl shadow-xl w-full max-w-md relative z-10">
        {/* Form State – Enter Email */}
        {pageState === 'form' && (
          <>
            {/* App Logo */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-brand-teal to-brand-indigo rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-text-primary">Delete Account</h1>
              <p className="text-text-secondary mt-2">
                Enter the email address associated with your Dough account. We'll email you a link to confirm the deletion.
              </p>
            </div>

            {/* Warning */}
            <div className="bg-system-error/10 border border-system-error/30 text-system-error px-4 py-3 rounded-xl text-sm mb-6">
              <strong>Warning:</strong> Account deletion is permanent. Your account, profile, progress, and saved goal plans will be removed from Dough's servers.
            </div>

            <form onSubmit={handleSubmitEmail} className="space-y-6">
              {formError && (
                <div className="bg-system-error/10 border border-system-error/30 text-system-error px-4 py-3 rounded-xl text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-bg-secondary border border-border-primary rounded-xl focus:ring-2 focus:ring-system-error focus:border-system-error outline-none transition-all text-text-primary placeholder-text-tertiary"
                  placeholder="Enter your email address"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-system-error text-white py-3 px-4 rounded-xl hover:opacity-90 focus:ring-4 focus:ring-system-error/30 transition-all font-medium"
              >
                Email Me a Confirmation Link
              </button>
            </form>
          </>
        )}

        {/* Sent State – check your email */}
        {pageState === 'sent' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-system-success/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-system-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Check Your Email</h1>
            <p className="text-text-secondary">
              If a Dough account uses <span className="font-medium text-text-primary">{email.trim()}</span>, we've sent it a link to confirm the deletion. The link works for 1 hour.
            </p>
            <p className="text-sm text-text-tertiary">
              Your account isn't deleted until you open the link and confirm. Didn't get an email? Check your spam folder, or wait a few minutes and try again.
            </p>
            <div className="pt-4">
              <button
                type="button"
                onClick={handleStartOver}
                className="w-full bg-bg-secondary text-text-secondary py-3 px-4 rounded-xl hover:bg-bg-tertiary transition-all font-medium border border-border-primary"
              >
                Use a Different Email
              </button>
            </div>
          </div>
        )}

        {/* Confirm State – opened from the emailed link */}
        {pageState === 'confirm' && (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-system-error/20 rounded-full mx-auto flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-system-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-text-primary">Are You Sure?</h1>
              <p className="text-text-secondary mt-2">
                This permanently deletes your account. Your profile, progress, and saved goal plans will be removed from Dough's servers and can't be restored.
              </p>
            </div>

            <div className="space-y-6">
              <button
                type="button"
                onClick={handleConfirmDeletion}
                className="w-full bg-system-error text-white py-3 px-4 rounded-xl hover:opacity-90 focus:ring-4 focus:ring-system-error/30 transition-all font-medium"
              >
                Delete My Account
              </button>

              <button
                type="button"
                onClick={handleStartOver}
                className="w-full bg-bg-secondary text-text-secondary py-3 px-4 rounded-xl hover:bg-bg-tertiary transition-all font-medium border border-border-primary"
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {/* Submitting State */}
        {(pageState === 'submitting' || pageState === 'requesting' || pageState === 'checking') && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <svg className="animate-spin h-12 w-12 text-system-error" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">
              {pageState === 'submitting' ? 'Deleting Account...' : pageState === 'checking' ? 'Checking Your Link...' : 'Sending...'}
            </h1>
            <p className="text-text-secondary">Please wait while we process your request. This may take a moment.</p>
          </div>
        )}

        {/* Success State */}
        {pageState === 'success' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-system-success/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-system-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Account Deleted</h1>
            <p className="text-text-secondary">
              Your account and all associated data have been permanently deleted.
            </p>
            <div className="pt-4">
              <p className="text-sm text-text-tertiary">You may now close this page.</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {pageState === 'error' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-system-error/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-system-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Something Went Wrong</h1>
            <p className="text-text-secondary">{errorMessage}</p>
            <div className="pt-4">
              <button
                onClick={handleStartOver}
                className="w-full bg-gradient-to-r from-brand-teal to-brand-indigo text-white py-3 px-4 rounded-xl hover:opacity-90 focus:ring-4 focus:ring-brand-teal/30 transition-all font-medium"
              >
                {link ? 'Request a New Link' : 'Try Again'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
